'use client'

import React, { useEffect, useMemo, useState } from 'react'

interface OnboardingProgressProps {
  currentStep: number
  totalSteps: number
  steps: Array<{ label: string; path: string }>
}

export const OnboardingProgress: React.FC<OnboardingProgressProps> = ({
  currentStep,
  totalSteps,
  steps,
}) => {
  const clampedStep = Math.min(Math.max(currentStep, 1), Math.max(totalSteps, 1))

  const targetProgress = useMemo(() => {
    // ✅ 기존 정책 유지: totalSteps=2면 Step2에서 50%
    if (totalSteps === 2) return ((clampedStep - 1) / totalSteps) * 100
    if (totalSteps <= 1) return 0
    return ((clampedStep - 1) / (totalSteps - 1)) * 100
  }, [clampedStep, totalSteps])

  const [animatedProgress, setAnimatedProgress] = useState(targetProgress)

  useEffect(() => {
    // ✅ 이전 값에서 목표 값까지 부드럽게 이동 (0으로 리셋 X)
    setAnimatedProgress((prev) => {
      // 같은 값이면 그대로
      if (Math.abs(prev - targetProgress) < 0.5) return targetProgress
      return prev
    })

    const duration = 450
    const frame = 16 // ~60fps
    const totalFrames = Math.max(1, Math.round(duration / frame))

    let i = 0
    const start = animatedProgress
    const delta = targetProgress - start

    const id = window.setInterval(() => {
      i += 1
      const t = i / totalFrames
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3)
      const next = start + delta * eased

      if (i >= totalFrames) {
        setAnimatedProgress(targetProgress)
        window.clearInterval(id)
      } else {
        setAnimatedProgress(next)
      }
    }, frame)

    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetProgress])

  const currentLabel = steps?.[clampedStep - 1]?.label

  return (
    <div className="w-full mb-8 space-y-2">
      {/* ✅ 텍스트로 현재 상태를 같이 보여줌 */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {currentLabel && <span className="font-medium text-gray-900">{currentLabel}</span>}
          <span className="ml-2 text-gray-500">
            {clampedStep}/{totalSteps}
          </span>
        </div>
        <div className="text-xs text-gray-500">{Math.round(targetProgress)}%</div>
      </div>

      {/* 진행률 바 */}
      <div
        className="w-full bg-gray-200 rounded-full h-2 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(animatedProgress)}
      >
        <div
          className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
          style={{ width: `${Math.max(0, Math.min(100, animatedProgress))}%` }}
        />
      </div>
    </div>
  )
}
